import type { ReactNode } from 'react';
import type { DataGridCheckRow } from '../../DataGrid/CheckCell/CheckCell';

import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import PopoverItem from '../../Popover/PopoverItem/PopoverItem';
import Tooltip from '../../Tooltip/Tooltip';

interface MassActionButtonProps {
  /**
   * Array of checked rows
   */
  checkedRows?: DataGridCheckRow[];
  /**
   * Handle click callback
   */
  handleClick?: () => void;
  /**
   * Button message
   */
  buttonMessage?: string;
  /**
   * Button color
   */
  color?: 'red' | 'blue' | 'green';
  /**
   * Button icon
   */
  icon?: ReactNode;
  /**
   * is disabled
   */
  disabled?: boolean;
  /**
   * If action is disallowed
   */
  actionDisallowed?: boolean;
  /**
   * testid
   */
  testId?: string;
}

const SELECTION_LIMIT = 100;

const MassActionButton = ({
  checkedRows,
  handleClick,
  buttonMessage,
  icon,
  disabled = false,
  actionDisallowed,
  color,
  testId,
}: MassActionButtonProps) => {
  const { t } = useTranslation();

  const limitExceeded = Boolean(
    checkedRows && checkedRows.length > SELECTION_LIMIT,
  );
  const actionDisabled = disabled || limitExceeded || actionDisallowed;

  let tooltipMessage: string | undefined;
  if (actionDisallowed) {
    tooltipMessage = t('UserRoles.CannotPerformAction');
  } else if (limitExceeded) {
    tooltipMessage = t('ObjectsOfType.MassActionLimitReached', {
      limit: SELECTION_LIMIT,
    });
  }

  const button = (
    <PopoverItem
      iconImage={icon}
      onClick={handleClick}
      buttonColor="borderless"
      iconPosition="start"
      color={color}
      disabled={actionDisabled}
      additionalClasses={twMerge(
        actionDisabled &&
          'bg-transparent hover:bg-transparent active:bg-transparent opacity-50 ',
      )}
      label={buttonMessage}
      testId={testId}
    />
  );

  if (!tooltipMessage) {
    return button;
  }

  return (
    <Tooltip
      tooltip={tooltipMessage}
      tooltipPlacement="rightCenter"
      phoneTooltipPlacement="rightCenter"
    >
      {button}
    </Tooltip>
  );
};

export default MassActionButton;
